import { motion } from "motion/react";
import { ArrowRight, Mail, Lock, ShieldCheck } from "lucide-react";
import { useState } from "react";

export function CaptureHero() {
  const [email, setEmail] = useState("");

  return (
    <section className="relative min-h-screen flex items-center justify-center py-20 overflow-hidden bg-[#0B0E14]">
      {/* Background Effects */}
      <div className="absolute inset-0 z-0">
        <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-[#00F5FF]/10 rounded-full blur-[100px]" />
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-[#FF4D00]/10 rounded-full blur-[100px]" />
        <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20" />
        
        {/* Grid */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#00F5FF_1px,transparent_1px),linear-gradient(to_bottom,#00F5FF_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)] opacity-10" />
      </div>

      <div className="relative z-10 container mx-auto px-4">
        <div className="max-w-4xl mx-auto text-center mb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#00F5FF]/10 border border-[#00F5FF]/30 text-[#00F5FF] text-sm font-mono mb-6"
          >
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#00F5FF] opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-[#00F5FF]"></span>
            </span>
            WPTT BETA ACCESS
          </motion.div>

          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-6xl lg:text-7xl font-bold text-white tracking-tight mb-6"
          >
            Pare de Perder Dinheiro <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#00F5FF] to-[#10B981]">
              No Seu WordPress
            </span>
          </motion.h1>

          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-lg md:text-xl text-gray-400 mb-8 max-w-2xl mx-auto"
          >
            Descubra a "Caixa Preta" do seu tráfego. Rastreie cada clique, identifique bots e otimize suas conversões com precisão forense.
          </motion.p>
        </div>

        {/* Capture Card */}
        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.3 }}
          className="max-w-md mx-auto bg-[#0B0E14]/80 backdrop-blur-xl border border-[#00F5FF]/30 rounded-2xl p-8 shadow-[0_0_50px_-12px_rgba(0,245,255,0.2)]"
        >
          <div className="text-center mb-6">
            <h3 className="text-xl font-bold text-white mb-2">Garanta Seu Acesso</h3>
            <p className="text-sm text-gray-400">Entre na lista de espera VIP e receba 50% de desconto no lançamento.</p>
          </div>

          <form className="space-y-4" onSubmit={(e) => e.preventDefault()}>
            <div className="relative">
              <Mail className="absolute left-3 top-3 w-5 h-5 text-gray-500" />
              <input 
                type="email" 
                placeholder="Seu melhor e-mail"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-[#1A1F2B] border border-gray-800 text-white pl-10 pr-4 py-3 rounded-lg focus:outline-none focus:border-[#00F5FF] focus:ring-1 focus:ring-[#00F5FF] transition-all placeholder-gray-600"
              />
            </div>

            <button className="w-full group relative bg-[#00F5FF] text-[#0B0E14] font-bold py-3 rounded-lg overflow-hidden transition-all hover:shadow-[0_0_20px_rgba(0,245,255,0.4)]">
              <div className="absolute inset-0 w-full h-full bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:animate-shimmer" />
              <span className="flex items-center justify-center gap-2">
                QUERO ENTRAR NA LISTA <ArrowRight className="w-4 h-4" />
              </span>
            </button>
          </form>

          <div className="mt-6 pt-6 border-t border-gray-800 flex items-center justify-center gap-6 text-xs text-gray-500">
            <div className="flex items-center gap-1">
              <Lock className="w-3 h-3" />
              Dados Criptografados
            </div>
            <div className="flex items-center gap-1">
              <ShieldCheck className="w-3 h-3" />
              100% Seguro
            </div>
          </div>
        </motion.div>

        {/* Social Proof Mini */}
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="mt-12 text-center"
        >
          <p className="text-sm text-gray-500 mb-4">Junte-se a +2.500 gestores de tráfego</p>
          <div className="flex justify-center -space-x-3">
             {[1, 2, 3, 4, 5].map((i) => (
               <div key={i} className="w-10 h-10 rounded-full border-2 border-[#0B0E14] bg-gray-800 flex items-center justify-center overflow-hidden">
                 <img 
                   src={`https://i.pravatar.cc/100?img=${10 + i}`} 
                   alt="User" 
                   className="w-full h-full object-cover"
                 />
               </div>
             ))}
             <div className="w-10 h-10 rounded-full border-2 border-[#0B0E14] bg-[#1A1F2B] flex items-center justify-center text-xs font-bold text-white">
               +2k
             </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
